import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../api/client';

const AuthContext = createContext(null);

const GOOGLE_CLIENT_ID = '451413422986-usr2gl1j5m5fi0ngclmo3kjv11p8p6sq.apps.googleusercontent.com';

// NOTE: Token storage uses localStorage because the remote backend expects
// Bearer tokens in Authorization headers. Moving to httpOnly cookies would
// require backend-side changes (cookie-based auth endpoints).
// Current mitigation: CSP headers + input sanitization prevent XSS vectors.

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const savedToken = localStorage.getItem('ahoow_token');
    const savedUser = localStorage.getItem('ahoow_user');
    if (savedToken && savedUser) {
      try {
        setToken(savedToken);
        setUser(JSON.parse(savedUser));
      } catch (e) {
        localStorage.removeItem('ahoow_token');
        localStorage.removeItem('ahoow_user');
      }
    }
    setLoading(false);
  }, []);

  const setSession = useCallback((t, u) => {
    localStorage.setItem('ahoow_token', t);
    localStorage.setItem('ahoow_user', JSON.stringify(u));
    setToken(t);
    setUser(u);
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    setSession(res.data.token, res.data.user);
    return res.data.user;
  }, [setSession]);

  const register = useCallback(async (data) => {
    const res = await api.post('/auth/register', data);
    setSession(res.data.token, res.data.user);
    return res.data.user;
  }, [setSession]);

  const loginWithGoogle = useCallback(async (googleToken) => {
    const res = await api.post('/auth/google', { google_token: googleToken });
    setSession(res.data.token, res.data.user);
    return res.data.user;
  }, [setSession]);

  const logout = useCallback(() => {
    localStorage.removeItem('ahoow_token');
    localStorage.removeItem('ahoow_user');
    setToken(null);
    setUser(null);
  }, []);

  const refreshProfile = useCallback(async () => {
    try {
      const res = await api.get('/profile/me');
      const updated = res.data;
      localStorage.setItem('ahoow_user', JSON.stringify(updated));
      setUser(updated);
    } catch (e) { /* ignore */ }
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, loginWithGoogle, logout, refreshProfile, GOOGLE_CLIENT_ID }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
